import * as v from 'valibot'
import { NovelPlayConfig, NovelPlayLanguage, NovelStudioBundle } from './play-config.js'
import { VisualGameTtsVoice } from '../visual-game.js'
import { NOVEL_STOREFRONT_LOCALES } from './storefront.js'

/** 作品状态；作者可改为私密或归档，公开须审核后由后台上架。 */
export const NOVEL_PACK_STATUSES = Object.freeze(['draft', 'published', 'unpublished', 'archived', 'deleted'])

/** 内容的严格校验结论（上架口径）：随每次保存重算，上架要求 valid。 */
export const NOVEL_VALIDATION_STATUSES = Object.freeze(['valid', 'invalid'])

/** 素材种类，对应 `novel_pack_assets.kind`。 */
export const NOVEL_ASSET_KINDS = Object.freeze(['cover', 'location', 'state_image', 'item'])

/**
 * 素材 tag：`<kind>` 或 `<kind>-<实体标识>`；前缀决定素材种类，整体进对象键
 * `novel/packs/<pack_id>/assets/<tag>/<hash>.<ext>`。
 */
export const NOVEL_ASSET_TAG_PATTERN = /^(cover|location|state_image|item)(-[a-z0-9_-]{1,80})?$/

export const NOVEL_ASSET_MIMES = Object.freeze(['image/png', 'image/jpeg', 'image/webp', 'image/gif'])

/** Studio preview uses server-owned sample prose; callers cannot submit arbitrary TTS text. */
export const NovelTtsVoicePreviewParams = v.strictObject({
  voice_id: v.pipe(v.string(), v.trim(), v.minLength(1), v.maxLength(120)),
})
export const NovelTtsVoicePreviewBody = v.strictObject({ language: NovelPlayLanguage })

/**
 * 一条严格校验错误：`code` 为引擎错误码，`path` 为出错字段的点分路径（如 `characters.1.name`，F7），`fields` 为键集差异。
 * 校验结果是全部问题的列表：每个实体、每个分区至少一条（S-3）。
 */
export const NovelValidationIssue = v.object({
  code: v.string(),
  path: v.nullable(v.string()),
  fields: v.nullable(v.object({
    missing: v.array(v.string()),
    unexpected: v.array(v.string()),
  })),
})

/**
 * 创作台作品行：内容保存只更新工作副本，content_hash 是当前内容的引擎哈希，validation_status 是当前内容的
 * 严格校验结论；listed_at 为最近一次上架时间，从未上架为 null；updated_at 为最近一次更新时间。
 */
export const NovelStudioPack = v.object({
  id: v.string(),
  slug: v.string(),
  owner_user_id: v.string(),
  title: v.string(),
  language: v.nullable(v.string()),
  source_language: v.string(),
  published_version_id: v.nullable(v.string()),
  status: v.picklist(NOVEL_PACK_STATUSES),
  cover_url: v.nullable(v.string()),
  cover_asset_id: v.optional(v.nullable(v.string()), null),
  // 按语言封面 { 语言: 素材 id }，草稿值；没配的语言用默认封面。
  cover_locales: v.optional(v.record(v.picklist(NOVEL_STOREFRONT_LOCALES), v.string()), () => ({})),
  content_hash: v.string(),
  // 可编辑状态（内容与音色映射）的修订号，每次保存 + 1；保存时作为 base_revision 带回做冲突检测（C21）。
  revision: v.pipe(v.number(), v.integer(), v.minValue(1)),
  validation_status: v.picklist(NOVEL_VALIDATION_STATUSES),
  latest_version: v.nullable(v.string()),
  listed_at: v.nullable(v.string()),
  created_at: v.string(),
  updated_at: v.string(),
})

/** 作者主动切出的不可变版本（形态 6.7）；发布到正式环境用它。 */
export const NovelStudioVersion = v.object({
  id: v.string(),
  version: v.string(),
  // 作者切版本时写的说明（C24），没写为 null。
  note: v.nullable(v.string()),
  package_hash: v.string(),
  published_at: v.string(),
  source_revision: v.nullable(v.number()),
  play_config_hash: v.nullable(v.string()),
  config_status: v.picklist(['frozen', 'legacy_unrecorded']),
})

/**
 * POST /api/novel/studio/packs/:id/versions（C24）：可选的版本说明，去首尾空白后最多 500 字，超出 400；
 * 去空白后为空按没写处理。
 * @typedef {{ base_revision: number, note?: string }} NovelVersionCreateBodyT
 */
export const NovelVersionCreateBody = v.strictObject({
  base_revision: v.pipe(v.number(), v.integer(), v.minValue(1)),
  note: v.optional(v.pipe(v.string(), v.trim(), v.maxLength(500))),
})

export const NovelStudioVersionList = v.object({
  items: v.array(NovelStudioVersion),
})

/** GET /api/novel/studio/packs/:id/versions/:version_id/export（C18，属主限定）：版本行与该版本冻结的完整内容。 */
export const NovelVersionExport = v.object({
  version: NovelStudioVersion,
  content: v.record(v.string(), v.unknown()),
  play_config: v.nullable(NovelPlayConfig),
  bundle_hash: v.string(),
})

export const NovelVersionResponse = v.object({
  version: NovelStudioVersion,
  pack: NovelStudioPack,
})

/** 创作台只列本人的作品（C7），一次取回全部。 */
export const NovelStudioPackList = v.object({
  items: v.array(NovelStudioPack),
})

export const NovelStudioAsset = v.object({
  id: v.string(),
  kind: v.picklist(NOVEL_ASSET_KINDS),
  storage_key: v.string(),
  public_url: v.string(),
  width: v.nullable(v.number()),
  height: v.nullable(v.number()),
  created_at: v.string(),
})

/** 人物 id → 平台音色 id，存 `novel_packs.voices_json`，不进包内。 */
export const NovelVoiceMap = v.record(v.string(), v.pipe(v.string(), v.minLength(1)))

/** 保存内容的结果：作品行、当前内容、音色映射（C20）与严格校验的全部错误（valid 时为空数组）。 */
export const NovelContentResponse = v.object({
  pack: NovelStudioPack,
  content: v.record(v.string(), v.unknown()),
  play_config: NovelPlayConfig,
  validation_errors: v.array(NovelValidationIssue),
})

/** 作品详情：保存结果的全部字段加素材与音色映射。版本由作者在创作台切出，另经 GET /packs/:id/versions 列出。 */
export const NovelStudioPackDetail = v.object({
  ...NovelContentResponse.entries,
  assets: v.array(NovelStudioAsset),
})

const NovelDefaultName = v.pipe(v.string(), v.trim(), v.minLength(1), v.maxLength(60))

/** 新建作品：无 `pack` 用空白模板，`names` 是作者界面语言下模板人物与地点的默认名；有 `pack` 宽松校验后作为作品内容（导入）。 */
export const NovelCreatePackBody = v.object({
  pack: v.optional(v.record(v.string(), v.unknown())),
  play_config: v.optional(NovelPlayConfig),
  names: v.optional(v.strictObject({ title: NovelDefaultName, player: NovelDefaultName, guide: NovelDefaultName, location: NovelDefaultName })),
})

/** Saves the working copy and play configuration under one revision. Published versions remain immutable. */
export const NovelContentBody = v.strictObject({
  pack: v.record(v.string(), v.unknown()),
  base_revision: v.pipe(v.number(), v.integer(), v.minValue(1)),
  play_config: v.optional(NovelPlayConfig),
  cover_asset_id: v.optional(v.nullable(v.pipe(v.string(), v.minLength(1)))),
})

export const NovelPlaytestBody = v.strictObject({
  base_revision: v.pipe(v.number(), v.integer(), v.minValue(1)),
  play_language: v.optional(v.picklist(['en', 'zh', 'ja', 'ko', 'es', 'pt'])),
  request_id: v.pipe(v.string(), v.uuid()),
  content: v.optional(v.record(v.string(), v.unknown())),
  play_config: v.optional(NovelPlayConfig),
})

/** Opening assistance uses the current local draft and returns a proposal without saving it. */
export const NovelOpeningAssistBody = v.strictObject({
  base_revision: v.pipe(v.number(), v.integer(), v.minValue(1)),
  pack: v.record(v.string(), v.unknown()),
  action: v.picklist(['draft', 'revise']),
  instructions: v.optional(v.pipe(v.string(), v.trim(), v.maxLength(4000))),
  request_id: v.pipe(v.string(), v.uuid()),
})

export const NovelOpeningAssistResponse = v.strictObject({
  opening: v.strictObject({
    segments: v.pipe(v.array(v.strictObject({
      speaker_id: v.pipe(v.string(), v.trim(), v.minLength(1)),
      channel: v.picklist(['narration', 'dialogue', 'action']),
      text: v.pipe(v.string(), v.trim(), v.minLength(1), v.maxLength(4000)),
    })), v.minLength(1)),
  }),
})

/** POST /api/novel/studio/packs/:id/validate（C5）：给了 pack 就校验请求体、不落库；没给就校验库里的当前内容。 */
export const NovelValidateBody = v.object({
  pack: v.optional(v.record(v.string(), v.unknown())),
})

export const NovelValidateResponse = v.object({
  valid: v.boolean(),
  errors: v.array(NovelValidationIssue),
})

export const NovelAssetUploadBody = v.object({
  tag: v.pipe(v.string(), v.regex(NOVEL_ASSET_TAG_PATTERN)),
  file_name: v.pipe(v.string(), v.minLength(1), v.maxLength(200)),
  mime: v.picklist(NOVEL_ASSET_MIMES),
  data_url: v.pipe(v.string(), v.minLength(1)),
})

export const NovelAssetResponse = v.object({
  asset: NovelStudioAsset,
  public_url: v.string(),
})

export const NovelVoicesBody = v.strictObject({
  base_revision: v.pipe(v.number(), v.integer(), v.minValue(1)),
  play_config: NovelPlayConfig,
})

export const NovelVoicesResponse = v.object({
  pack: NovelStudioPack,
  play_config: NovelPlayConfig,
})

export const NovelTtsVoiceList = v.object({
  voices: v.array(VisualGameTtsVoice),
})

/** 导出体即作品当前内容本身。 */
export const NovelStudioExport = NovelStudioBundle

/** Soft deletion retains content and saved runs; repeated owner deletion is idempotent. */
export const NovelDeleteResponse = v.object({
  deleted: v.boolean(),
  pack_id: v.string(),
})

/** 按语言封面整份替换：值是本作品已上传的封面素材 id，不在表里的语言回退默认封面。 */
export const NovelCoverLocalesBody = v.strictObject({
  cover_locales: v.record(v.picklist(NOVEL_STOREFRONT_LOCALES), v.pipe(v.string(), v.minLength(1))),
})

export const NovelCoverLocalesResponse = v.object({
  pack: NovelStudioPack,
})

/** 作者归档（形态 6.6 任意状态 → archived）后的作品行。 */
export const NovelArchiveResponse = v.object({
  pack: NovelStudioPack,
})

export const NovelReview = v.object({
  id: v.string(), title: v.string(), pack_id: v.string(), version_id: v.string(), submitted_by: v.string(),
  source_revision: v.number(), source_hash: v.string(), source_config_hash: v.string(),
  snapshot_hash: v.string(), cover_asset_id: v.nullable(v.string()), cover_url: v.nullable(v.string()),
  status: v.picklist(['pending', 'approved', 'rejected', 'withdrawn']),
  preparation_status: v.picklist(['unprepared', 'preparing', 'ready', 'failed']),
  preparation_error: v.nullable(v.string()), reason: v.nullable(v.string()),
  reviewed_by: v.nullable(v.string()), reviewed_at: v.nullable(v.string()),
  created_at: v.string(), updated_at: v.string(),
})
export const NovelReviewResponse = v.object({ review: NovelReview })
export const NovelReviewList = v.object({ items: v.array(NovelReview) })
export const NovelReviewSubmitBody = v.strictObject({
  base_revision: v.pipe(v.number(), v.integer(), v.minValue(1)),
  request_id: v.optional(v.pipe(v.string(), v.uuid())),
})
export const NovelReviewDecisionBody = v.strictObject({
  decision: v.picklist(['approved', 'rejected']),
  reason: v.optional(v.pipe(v.string(), v.trim(), v.maxLength(2000))),
  snapshot_hash: v.pipe(v.string(), v.minLength(1), v.maxLength(128)),
})
export const NovelReviewPublishBody = v.strictObject({ review_id: v.pipe(v.string(), v.minLength(1)) })
export const NovelReviewDetail = v.object({
  review: NovelReview,
  content: v.record(v.string(), v.unknown()),
  play_config: NovelPlayConfig,
  prepared_play_config: v.nullable(NovelPlayConfig),
  assets: v.array(NovelStudioAsset),
})
